/**
 * Islands runtime entry. The Vite library build emits this file as
 * `dist-islands/islands.js`; the legacy `index.html` loads it via
 * `<script type="module">`. On load it registers `window.ShaunIslands` and
 * the legacy `switchTab()` calls into `mount()` for migrated tabs.
 *
 * Critical ordering: the supabase client module is imported lazily inside
 * `mount()` so it runs AFTER `window.__sbClient = sb` has been set on the
 * legacy side. See R3 in /root/.claude/plans/go-with-second-one-imperative-owl.md.
 */

// Activates the shared design-token CSS bundle. Side-effect import: pulls
// shared/tokens/tokens.css into the Vite build so dist-islands/islands.css
// is emitted and (via the <link> tag in crm/index.html) loaded by the host
// page. Lives here, not in any individual primitive, so future primitives
// can consume tokens without re-wiring.
import '../shared/tokens'

import { createElement } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { IslandRoot } from './IslandRoot'
import { installHostToastBridge } from '../lib/toast'
import { installOAuthRelay } from '../lib/oauthRelay'
import { loadIsland, type IslandName, type IslandProps } from './registry'
import type { ImportedCustomRow } from './outreach/legacyBridge'
import type { CustomFieldDef } from '../lib/outreach/types'

interface IslandHandle {
  update(props: IslandProps): void
  unmount(): void
}

interface ShaunIslandsApi {
  mount(name: IslandName, el: HTMLElement, props: IslandProps): IslandHandle
  unmount(handle: IslandHandle): void
  events: EventTarget
  /** Custom field helpers for the legacy CSV import (loaded on first use). */
  customFields: {
    loadContactDefs(): Promise<CustomFieldDef[]>
    saveImported(
      rows: ImportedCustomRow[],
      defs: CustomFieldDef[],
    ): Promise<{ saved: number; errors: string[] }>
  }
}

const events = new EventTarget()

function mount(name: IslandName, el: HTMLElement, props: IslandProps): IslandHandle {
  el.innerHTML = ''
  const root: Root = createRoot(el)
  let currentProps: IslandProps = props
  let unmounted = false

  // Render a lightweight loading state synchronously while the component
  // module loads. This is fine — `loadIsland` resolves on the next tick.
  root.render(createElement(IslandRoot, { user: props.user }, 'Loading…'))

  void loadIsland(name).then((Component) => {
    if (unmounted) return
    root.render(
      createElement(
        IslandRoot,
        { user: currentProps.user },
        createElement(Component, currentProps),
      ),
    )
  })

  return {
    update(next) {
      currentProps = next
      void loadIsland(name).then((Component) => {
        if (unmounted) return
        root.render(createElement(IslandRoot, { user: next.user }, createElement(Component, next)))
      })
    },
    unmount() {
      unmounted = true
      root.unmount()
    },
  }
}

const api: ShaunIslandsApi = {
  mount,
  unmount(handle) {
    handle.unmount()
  },
  events,
  customFields: {
    async loadContactDefs() {
      return (await import('./outreach/legacyBridge')).loadContactDefs()
    },
    async saveImported(rows, defs) {
      return (await import('./outreach/legacyBridge')).saveImported(rows, defs)
    },
  },
}

window.ShaunIslands = api

// Islands call `lib/toast`; the visible toast is the host's `window.toast()`.
// Without this bridge, island toasts had no subscriber and showed nothing.
installHostToastBridge()
installOAuthRelay()

// Auto-mount the persistent shell islands. Each lives in the DOM once
// per session and short-circuits to null pre-auth (so the React tree is
// trivial on the auth screen). The mount points live inside `#app` which
// the auth flow toggles display:none → block.
{
  const w = window as unknown as { user?: { id: string; email?: string } | null }
  const initialUser = w.user ?? null

  const railMount =
    typeof document !== 'undefined' ? document.getElementById('app-rail-mount') : null
  if (railMount) {
    api.mount('app-rail', railMount, { user: initialUser })
  }

  const cmdkMount = typeof document !== 'undefined' ? document.getElementById('cmd-k-mount') : null
  if (cmdkMount) {
    api.mount('command-palette', cmdkMount, { user: initialUser })
  }
}
